import { Heart } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/shared/lib/utils";
import type { Section } from "./types";
import { useSavedComponentsOptional } from "./SavedComponentsProvider";

interface SaveButtonProps {
  section: Section;
  panelQuestion: string;
}

/**
 * Hover/focus-revealed heart toggle on a likeable component's header. The
 * pressed state is announced via `aria-pressed` (the label stays constant — an
 * APG toggle, not a relabel). Optimistic: the localStorage write is synchronous
 * so there is no spinner. Renders nothing when no SavedComponents provider is
 * mounted (isolated tests).
 */
export function SaveButton({ section, panelQuestion }: SaveButtonProps) {
  const store = useSavedComponentsOptional();
  const reduce = useReducedMotion();
  if (!store) return null;
  const saved = store.isSaved(section);

  return (
    <button
      type="button"
      aria-pressed={saved}
      aria-label="Save to My Dashboard"
      title="Save to My Dashboard"
      onClick={(e) => {
        e.stopPropagation();
        store.toggle(section, panelQuestion);
      }}
      className={cn(
        "grid h-7 w-7 shrink-0 place-items-center rounded-md transition-opacity",
        "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "opacity-0 group-hover/shell:opacity-100 focus-visible:opacity-100",
        saved ? "text-primary opacity-100" : "text-muted-foreground",
      )}
    >
      <motion.span
        animate={{ scale: saved && !reduce ? [1, 1.25, 1] : 1 }}
        transition={{ duration: reduce ? 0 : 0.22, ease: [0.05, 0.7, 0.1, 1] }}
        className="grid place-items-center"
      >
        <Heart className={cn("h-4 w-4", saved && "fill-current")} />
      </motion.span>
    </button>
  );
}
